import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { MusicNFT } from '../../types/blockchain';
import blockchainService from '../../services/blockchainService';
import { 
  MusicalNoteIcon, 
  CurrencyDollarIcon, 
  HeartIcon,
  ShoppingCartIcon,
  SparklesIcon 
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const NFTMarketplace: React.FC = () => {
  const { wallet } = useWeb3();
  const [myNFTs, setMyNFTs] = useState<MusicNFT[]>([]);
  const [marketplaceNFTs, setMarketplaceNFTs] = useState<MusicNFT[]>([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);
  const [activeTab, setActiveTab] = useState<'marketplace' | 'my-nfts' | 'mint'>('marketplace');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadNFTData();
  }, [wallet]);

  const loadNFTData = async () => {
    setLoading(true);
    try {
      // 使用新的统一mock数据
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      
      const [marketplace, myNFTsData] = await Promise.all([
        Promise.resolve(mockData.getMusicWorkNFTs()), // 获取所有NFT作为市场数据
        wallet?.address ? Promise.resolve(mockData.getMusicWorkNFTs(wallet.address)) : Promise.resolve([])
      ]);
      
      // 转换为MusicNFT格式
      const marketplaceNFTs: MusicNFT[] = marketplace.map(work => ({
        id: work.id,
        tokenId: work.tokenId || '0',
        workId: work.workId,
        title: work.title,
        description: work.description,
        image: work.coverImage,
        audioUrl: work.audioUrl,
        creator: work.participants?.[0]?.name || 'Unknown Artist',
        owner: work.participants?.[0]?.name || 'Unknown Artist',
        price: work.floorPrice?.toString() || '0.5',
        currency: 'SOL' as const,
        rarity: 'common' as const,
        mintedAt: work.createdAt || Date.now() - Math.random() * 86400000 * 30,
        isForSale: true,
        saleHistory: []
      }));
      
      setMarketplaceNFTs(marketplaceNFTs);
      setMyNFTs(myNFTsData.map(work => ({
        id: work.id,
        tokenId: work.tokenId || '0',
        workId: work.workId,
        title: work.title,
        description: work.description,
        image: work.coverImage,
        audioUrl: work.audioUrl,
        creator: work.participants?.[0]?.name || 'Unknown Artist',
        owner: work.participants?.[0]?.name || 'Unknown Artist',
        price: work.floorPrice?.toString() || '0.5',
        currency: 'SOL' as const,
        rarity: 'common' as const,
        mintedAt: work.createdAt || Date.now() - Math.random() * 86400000 * 30,
        isForSale: false,
        saleHistory: []
      })));
    } catch (error) {
      console.error('Failed to load NFT data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMintNFT = async (formData: any) => {
    if (!wallet?.address) return;
    
    setMinting(true);
    try {
      const nft = await blockchainService.mintMusicNFT('work_new', {
        title: formData.title,
        description: formData.description,
        image: '/images/nft-placeholder.jpg',
        audioUrl: '/audio/placeholder.mp3',
        creator: wallet.address,
        rarity: formData.rarity
      });
      
      setMyNFTs(prev => [nft, ...prev]);
      setActiveTab('my-nfts');
    } catch (error) {
      console.error('Failed to mint NFT:', error);
    } finally {
      setMinting(false);
    }
  };

  const toggleFavorite = (nftId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(nftId)) {
        newFavorites.delete(nftId);
      } else {
        newFavorites.add(nftId);
      }
      return newFavorites;
    });
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'text-gray-600 bg-gray-100';
      case 'rare': return 'text-blue-600 bg-blue-100';
      case 'epic': return 'text-purple-600 bg-purple-100';
      case 'legendary': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRarityIcon = (rarity: string) => {
    const iconClass = "w-4 h-4";
    switch (rarity) {
      case 'legendary': return <SparklesIcon className={`${iconClass} text-yellow-500`} />;
      default: return <MusicalNoteIcon className={`${iconClass} text-gray-500`} />;
    }
  };

  const NFTCard: React.FC<{ nft: MusicNFT; showBuyButton?: boolean }> = ({ nft, showBuyButton = false }) => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative">
        <img 
          src={nft.image} 
          alt={nft.title}
          className="w-full h-48 object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/nft-placeholder.jpg';
          }}
        />
        <button
          onClick={() => toggleFavorite(nft.id)}
          className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white"
        >
          {favorites.has(nft.id) ? (
            <HeartSolidIcon className="w-5 h-5 text-red-500" />
          ) : (
            <HeartIcon className="w-5 h-5 text-gray-600" />
          )}
        </button>
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${getRarityColor(nft.rarity)}`}>
            {getRarityIcon(nft.rarity)}
            {nft.rarity}
          </span>
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-2">{nft.title}</h3>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{nft.description}</p>
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm text-gray-500">创作者</span>
            <p className="font-medium text-sm">{nft.creator.substring(0, 10)}...</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-500">价格</span>
            <p className="font-semibold text-lg">{nft.price} {nft.currency}</p>
          </div>
        </div>
        
        {showBuyButton && nft.isForSale && (
          <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
            <ShoppingCartIcon className="w-4 h-4" />
            购买 NFT
          </button>
        )}
        
        {!showBuyButton && (
          <div className="flex gap-2">
            <button className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">
              查看详情
            </button>
            <button className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">
              {nft.isForSale ? '修改价格' : '上架销售'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const MintForm: React.FC = () => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      rarity: 'common'
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleMintNFT(formData);
    };

    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">铸造音乐 NFT</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">NFT 标题</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">描述</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">稀有度</label>
            <select
              value={formData.rarity}
              onChange={(e) => setFormData({...formData, rarity: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="common">普通</option>
              <option value="rare">稀有</option>
              <option value="epic">史诗</option>
              <option value="legendary">传奇</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={minting}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {minting ? '铸造中...' : '铸造 NFT'}
          </button>
        </form>
      </div>
    );
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以使用 NFT 市场</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">NFT 音乐市场</h1>
        <p className="text-gray-600">发现、购买和交易独特的音乐NFT</p>
        
        {/* NFT投资价值提示 */}
        <div className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <span className="text-2xl">💎</span>
            </div>
            <div>
              <h3 className="font-semibold text-orange-800 mb-2">NFT投资价值</h3>
              <p className="text-sm text-orange-700 mb-2">
                早期购买音乐NFT具有投资价值！随着艺术家知名度提升和作品流行度增加，NFT价值可能显著增长。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-orange-600">
                <div>• 早期投资者平均ROI: <span className="font-bold text-green-600">+145%</span></div>
                <div>• 热门作品增值幅度: <span className="font-bold text-green-600">+280%</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 标签页导航 */}
      <div className="bg-gray-100 p-1 rounded-lg mb-8 flex">
        {[
          { key: 'marketplace', label: 'NFT 市场' },
          { key: 'my-nfts', label: '我的 NFT' },
          { key: 'mint', label: '铸造 NFT' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 内容区域 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">加载中...</p>
        </div>
      ) : (
        <>
          {activeTab === 'marketplace' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">市场上的 NFT</h2>
              {marketplaceNFTs.length === 0 ? (
                <p className="text-center text-gray-500 py-12">暂无 NFT 在售</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {marketplaceNFTs.map((nft) => (
                    <NFTCard key={nft.id} nft={nft} showBuyButton={true} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'my-nfts' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">我的 NFT 收藏</h2>
              {myNFTs.length === 0 ? (
                <p className="text-center text-gray-500 py-12">您还没有任何 NFT</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {myNFTs.map((nft) => (
                    <NFTCard key={nft.id} nft={nft} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'mint' && <MintForm />}
        </>
      )}
    </div>
  );
};

export default NFTMarketplace;
